import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SIGN_MAPPING } from 'utils/signs';
import { event } from '@minerva-analytics/pantheon';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import getCompatibility from 'store/compatibility/actions';
import { ShadowContentCard, StyledButton } from '../../styled_constants';
import capitalize from '../../oldpantheon/utils/capitalize';
import CompatibilityImage from '../../assets/compatibility_icon.png';
import HeartImage from '../../assets/symbols_love-active.png';
import SignTable from '../../components/SignTable';
import CompatibilityReport from '../../components/CompatibilityReport';
import {
  PageHeader,
  PageSubtitle,
  CompatibilityIcon,
  FullHeaderWrapper,
  PageWrapper,
  InputTitle,
  InputWrapper,
  StyledSelect,
  SingleInputWrapper,
  HeartCompatibilityIcon,
  InfoTitle,
  InfoBodyCopy,
  HelpBlock,
  SignTableTitle,
} from './Compatibility.styles';


export const Compatibility = (props) => {
  const [fullCompatibility, setFullCompatibility] = useState(false);
  const [validationState, setValidationState] = useState(true);
  const [yourSelectedSign, setYourSelectedSign] = useState(``);
  const [partnerSelectedSign, setPartnerSelectedSign] = useState(``);

  const selectYourSign = Object.keys(SIGN_MAPPING).map((s) => ({
    label: capitalize(s),
    value: s,
  }));
  selectYourSign.unshift({ label: `Select your sign`, value: `` });

  const selectPartnerSign = Object.keys(SIGN_MAPPING).map((s) => ({
    label: capitalize(s),
    value: s,
  }));
  selectPartnerSign.unshift({ label: `Select your partner's sign`, value: `` });

  const postCompatibility = () => {
    if (!yourSelectedSign || !partnerSelectedSign) {
      setValidationState(false);
    }

    if (yourSelectedSign && partnerSelectedSign) {
      setValidationState(true);
      props.getCompatibility(yourSelectedSign, partnerSelectedSign);
      setFullCompatibility(true);
    }
  };

  const setSignsButtonClick = () => {
    event([`click`, `compatibility`]);
    postCompatibility();
  };

  return (
    <>
      <Helmet>
        <title>Zodiac Compatibility Calculator - Starguide</title>
        <meta name="og:title" property="og:title" content="Zodiac Compatibility Calculator - Starguide" />
        <meta name="og:description" property="og:description" content="Are you a match? Find out if you and your sweetheart are compatible with our Compatibility Calculator and see which signs are best for you!" />
        <meta name="description" property="description" content="Are you a match? Find out if you and your sweetheart are compatible with our Compatibility Calculator and see which signs are best for you!" />
      </Helmet>
      {/* <AdWrapper>
        <DFPAdSenseUnit banner adUnit="starguide_calculator_top_banner" className="center" />
      </AdWrapper> */}
      <PageWrapper>
        <FullHeaderWrapper>
          <CompatibilityIcon src={ CompatibilityImage } alt="Compatibility icon for the compatibility calculator" height="130" />
          <div>
            <PageHeader>
              Zodiac Compatibility
            </PageHeader>
            <PageSubtitle>
              Find out if you and your partner are compatible for love
            </PageSubtitle>
          </div>
        </FullHeaderWrapper>
      </PageWrapper>
      <ShadowContentCard>
        <InputWrapper>
          <SingleInputWrapper>
            <InputTitle>
              Your sign
            </InputTitle>
            <StyledSelect
              items={ selectYourSign }
              onChange={ (e) => setYourSelectedSign(e.target.value) }
              value={ props.yourSign }
            />
          </SingleInputWrapper>
          <HeartCompatibilityIcon src={ HeartImage } alt={`Heart icon to show compatibility between ${props.yourSign} and ${props.partnerSign}`} height="35" />
          <SingleInputWrapper>
            <InputTitle>
              Your partner&apos;s sign
            </InputTitle>
            <StyledSelect
              items={ selectPartnerSign }
              onChange={ (e) => setPartnerSelectedSign(e.target.value) }
              value={ props.partnerSign }
            />
          </SingleInputWrapper>
        </InputWrapper>
        <HelpBlock validationState={ validationState }>
          { validationState ? `` : `Please select a sign for both you and your partner to find your compatibility` }
        </HelpBlock>
        <StyledButton
          type="submit"
          onClick={ setSignsButtonClick }
        >
          Are you a match?
        </StyledButton>
        { fullCompatibility
          ? <CompatibilityReport />
          : null }
      </ShadowContentCard>
      <PageWrapper>
        <InfoTitle>
          What Is Zodiac Compatibility?
        </InfoTitle>
        <InfoBodyCopy>
          Your zodiac compatibility – or horoscope compatibility –
          can help you get a sense of how you and a potential partner
          would be together in a long-term relationship. Would you
          butt heads, or hum along together in an easy camaraderie?
          Do you have complementary emotional needs, or would you each
          struggle to fulfill your partner’s desires? Do you like to
          live life at the same pace, or would you constantly be out
          of balance with each other? Our Zodiac Compatibility Calculator
          can help you get a sense of what the answers to these questions might be.
        </InfoBodyCopy>
        <InfoTitle>
          Why Calculate Your Astrological Compatibility?
        </InfoTitle>
        <InfoBodyCopy>
          Everyone wants to fall in love with their best friend, their ideal partner,
          their soulmate. It’s not uncommon to wonder if you and a new or existing partner
          would be compatible long-term. Your zodiac compatibility can help you figure out
          how likely a successful, mutually beneficial relationship is between two signs.
        </InfoBodyCopy>
        <InfoTitle>
          How Is Your Zodiac Compatibility Calculated?
        </InfoTitle>
        <InfoBodyCopy>
          Astrological compatibility is calculated based on the personality traits
          of two given signs and the ease with which they can likely form a comfortable,
          harmonious relationship. Our Love Compatibility Calculator uses your sun sign
          rather than your moon sign or ascendant sign. It is said that your sun sign
          speaks to both your inner and outer personalities, so you can rest assured
          that our Astrology Compatibility Calculator takes into account both initial
          physical attraction as well the potential for a deeper emotional connection that lasts.
        </InfoBodyCopy>
        {/* <AdWrapper>
          <DFPAdSenseUnit banner adUnit="starguide_calculator_mid_text" className="center" />
        </AdWrapper> */}
        <InfoTitle>
          Are Some Signs More Compatible Than Others?
        </InfoTitle>
        <InfoBodyCopy>
          Horoscope compatibility is complicated, but the short answer is, yes! Think about
          it this way: Each astrological sign has a unique set of personality traits. Just
          as some personalities tend to get along better than others, certain zodiac signs
          may be better suited to romantic relationships than others. In any relationship,
          it’s important to be able to meet one another’s needs, enjoy similar lifestyles,
          and compromise effectively. Calculating two astrological signs’ compatibility
          together can help you get a sense of how easy or difficult it would be to form
          a mutually satisfying, lasting relationship.
        </InfoBodyCopy>
        <InfoTitle>Which Signs Are Most Compatible?</InfoTitle>
        <InfoBodyCopy>
          Horoscope compatibility can be very intricate, but for each zodiac sign, there are
          usually two or three other signs that are especially compatible. Signs from within
          the same element of either Earth, Fire, Air, or Water also tend to be very compatible.
          Interestingly, many astrologers note that Fire and Air signs tend to be complimentary,
          just like oxygen and fire strengthen each other in the natural world. Earth and Water
          pairings also tend to have high levels of compatibility – think of the way rain and
          plants nourish one another. Couples who share the exact same sign can also expect to
          have high levels of compatibility in most cases. Want to know the compatibility score
          for you and your partner? Try our Astrological Compatibility Calculator and see!
        </InfoBodyCopy>
        <InfoTitle>What Should I Do If My Partner and I Are Not Compatible?</InfoTitle>
        <InfoBodyCopy>
          It’s important to remember that every relationship is different and there are always
          exceptions to every rule. If you love your partner and are happy together, don&apos;t fret
          if your zodiac compatibility suggests opposition. There can be quite a bit of truth
          in the age-old saying that opposites attract! But if you find yourself constantly
          miserable or if you feel your relationship is unhealthy, don’t hesitate to reach
          out to a trusted loved one or a trained counselor for help. And remember that no
          matter what your astrological compatibility score says, all relationships take work
          and require compromise and open communication. In some cases, you may just need to work
          a bit harder on openly communicating and empathizing with your partner!
        </InfoBodyCopy>
        {/* <AdWrapper>
        <DFPAdSenseUnit banner adUnit="starguide_calculator_above_sign_table" className="center" />
        </AdWrapper> */}
        <SignTableTitle>
          Read today&apos;s horoscopes
        </SignTableTitle>
        <SignTable />
      </PageWrapper>
      {/* <AdWrapper>
        <DFPAdSenseUnit banner adUnit="starguide_calculator_below_sign_table" className="center" />
      </AdWrapper> */}
    </>
  );
};

Compatibility.propTypes = {
  getCompatibility: PropTypes.func,
  yourSign: PropTypes.string,
  partnerSign: PropTypes.string,
};

export default connect(() => ({}), {
  getCompatibility,
})(Compatibility);
